import { useCurrentUser } from "components/CurrentUser/hooks";
import {
  IUpdatePasswordRequest,
  IUserInfo,
  IUserInfoUpdation,
} from "components/CurrentUser/services/typings";
import userService from "components/CurrentUser/services/userService";
import { useState, useCallback, useEffect, useRef } from "react";
import useSWR, { SWRConfiguration, mutate } from "swr";

export const useUpdateUserInfo = () => {
  const [loading, setLoading] = useState(false);

  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  const update = useCallback(
    async (req: IUserInfoUpdation): Promise<IUserInfo> => {
      try {
        setLoading(true);
        const res = await userService.updateInfo(req);
        !!res && mutate("USER_INFO", (previous) => ({ ...previous, ...res }));
        mounted.current && setLoading(false);
        return res;
      } catch (e) {
        mounted.current && setLoading(false);
        throw e;
      }
    },
    [setLoading]
  );

  return [update, loading] as const;
};

export const useUpdateUserBillingInfo = () => {
  const [loading, setLoading] = useState(false);

  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  const update = useCallback(
    async (req: IUserInfoUpdation): Promise<IUserInfo> => {
      try {
        setLoading(true);
        const res = await userService.updateBillingInfo(req);
        !!res && mutate("USER_INFO", (previous) => ({ ...previous, ...res }));
        mounted.current && setLoading(false);
        return res;
      } catch (e) {
        mounted.current && setLoading(false);
        throw e;
      }
    },
    [setLoading]
  );

  return [update, loading] as const;
};

export const useUserInfo = (config: SWRConfiguration = {}) => {
  const [user] = useCurrentUser({ revalidateOnMount: false });
  const { data, error, mutate } = useSWR<IUserInfo>(
    user?.id ? "USER_INFO" : null,
    userService.getInfo,
    {
      dedupingInterval: 3000,
      ...config,
    }
  );
  const loading = user?.id ? data === undefined && !error : false;
  return {
    data,
    loading,
    error,
    mutate,
  };
};

export const useUpdatePassword = () => {
  const [loading, setLoading] = useState(false);

  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  const update = useCallback(
    async (req: IUpdatePasswordRequest): Promise<null> => {
      try {
        setLoading(true);
        await userService.updatePassword(req);
        mounted.current && setLoading(false);
        return null;
      } catch (e) {
        mounted.current && setLoading(false);
        throw e;
      }
    },
    [setLoading]
  );

  return [update, loading] as const;
};
